// PR 4 — idempotency-key helper.
//
// PR #398 shipped BE-side dedup: POST /api/takeover/sessions accepts
// `idempotencyKey` and returns the same `sessionId` on retry, plus a
// `rejoined: true` flag when the dedup-cache hit. The FE generates the
// key once on first visit and persists it in localStorage so a refresh /
// double-click on the Welcome CTA produces the same key — hence the
// same session — instead of two parallel sessions for the same seller.
//
// Key shape: standard UUID v4 (lowercase, hyphenated). The BE accepts
// any opaque string up to its configured cap; UUID v4 is the simplest
// universal anti-collision shape and matches what the operator
// dashboard expects in its dedup logs.

const STORAGE_KEY = 'takeover.idempotencyKey';

// crypto.randomUUID() is available in every browser the takeover-app
// targets (CRA browserslist excludes IE / op_mini), but the test
// environment (jsdom under react-scripts) may not provide it on the
// global crypto polyfill. Fall back to a small RFC4122-shaped generator
// so unit tests can exercise the helper without a browser.
function generateUuidV4() {
  if (typeof globalThis !== 'undefined'
      && globalThis.crypto
      && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Math.random is fine for FE de-dup keys — we're not signing anything;
  // collision risk for a single seller's two-tab race is astronomical.
  const hex = (n) => Math.floor(Math.random() * n).toString(16);
  const seg = (len) => Array.from({ length: len }, () => hex(16)).join('');
  // Set version (4) + variant (8/9/a/b) bits per RFC 4122 §4.4.
  const v = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${seg(8)}-${seg(4)}-4${seg(3)}-${v}${seg(3)}-${seg(12)}`;
}

/**
 * Returns the seller's idempotency key, generating + persisting one on
 * first call. Subsequent calls (including across page reloads) return
 * the same key.
 *
 * The key is intentionally NOT cleared on session completion — once the
 * seller is past the BE dedup window (default 24h) the key is harmless
 * to keep around, and the BE will just treat the next POST as a fresh
 * session anyway (a different localStorage `takeover.sessionId` would
 * have been cleared by the success path). Clearing here would defeat
 * the dedup purpose: a refresh during the same takeover would produce
 * a brand-new key and bypass the cache.
 *
 * Pass a custom storage backend for unit tests; production calls pass
 * nothing and we read from window.localStorage.
 */
export function getOrCreateIdempotencyKey(storage) {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!store) {
    // SSR / non-browser environment — return a one-off key. The caller
    // (createSession) will get a non-persisted UUID; harmless because
    // SSR never actually submits a takeover.
    return generateUuidV4();
  }
  try {
    const existing = store.getItem(STORAGE_KEY);
    if (existing && typeof existing === 'string' && existing.length > 0) {
      return existing;
    }
  } catch {
    // Private-mode Safari, storage quota, etc. — fall through to a
    // one-off key. Same SSR rationale: harmless.
    return generateUuidV4();
  }
  const fresh = generateUuidV4();
  try {
    store.setItem(STORAGE_KEY, fresh);
  } catch {
    /* see above — storage failure is non-fatal */
  }
  return fresh;
}

/**
 * Test/operator escape hatch — wipes the persisted key so the next
 * call to getOrCreateIdempotencyKey() returns a fresh UUID. Not used
 * in the seller flow (the BE dedup window does the de-collision work);
 * exposed for unit tests and for the future "clear session" support
 * action.
 */
export function clearIdempotencyKey(storage) {
  const store = storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export const __STORAGE_KEY = STORAGE_KEY;
